import { ArrowForward } from "@mui/icons-material";
import { Card, Divider, MenuItem, Select, Stack, Switch, Typography } from "@mui/material";
import React, { type ChangeEvent, useEffect, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { HeaderLayout } from "~/components/layout/HeaderLayout";
import { DeletePresetModal } from "~/components/market-data/market-data-filters/DeletePresetsModal";
import { Button } from "~/components/ui/core/Button";
import { CardHeader } from "~/components/ui/core/CardHeader";
import { NextLinkComposed } from "~/components/ui/core/Link";
import { JobSelector } from "~/components/ui/JobSelector";
import { BenchmarkLevelSelector } from "~/components/ui/level/BenchmarkLevelSelector";
import { LevelSelector } from "~/components/ui/LevelSelector";
import { type LocationForSelector, LocationSelector } from "~/components/ui/LocationSelector";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { useSession } from "~/hooks/useSession";
import { useI18n } from "~/lib/i18n/useI18n";
import { uniq } from "~/lib/lodash";
import { useFetchFiltersPresetsQuery } from "~/pages/api/filters-preset/fetch-filters-presets";
import { useGetDefaultLocationsQuery } from "~/pages/api/market-data-search/get-default-locations";
import { type EmployeeLevel, canAccessLevel } from "~/services/employee/employeeLevel";
import { type ConvertFiltersPresetsToInputResult } from "~/services/filters-preset/convertFiltersPresetsToInput";

type Props = {
  useTestData: boolean;
  filtersPresets: ConvertFiltersPresetsToInputResult;
  onSubmit: (input: Input) => void;
  onChange: (input: Input) => void;
};

type Input = {
  jobIds: string[];
  levels: EmployeeLevel[];
  benchmarkLevelIds: number[];
  locations: LocationForSelector[];
  presetId?: number;
};

const DEFAULT_LEVELS: EmployeeLevel[] = ["JUNIOR", "INTERMEDIATE", "SENIOR"] as const; // To be deleted when Level Framework is fully live.

export const MarketDataForm: React.FC<Props> = ({
  useTestData,
  filtersPresets: initialFiltersPresets,
  onSubmit,
  onChange,
}) => {
  const { t } = useI18n();
  const { user } = useSession();
  const { CAN_ACCESS_LEVEL_FRAMEWORKS } = useFeatureFlags();
  const [hasInteractedWithLevels, setHasInteractedWithLevels] = useState(false);
  const [hasInteractedWithLocations, setHasInteractedWithLocations] = useState(false);
  const [isDisclaimerAccepted, setIsDisclaimerAccepted] = useState(!useTestData);
  const [isDisclaimerReallyAccepted, setIsDisclaimerReallyAccepted] = useState(!useTestData);
  const [input, setInput] = useState<Input>({
    jobIds: [],
    levels: [],
    locations: [],
    benchmarkLevelIds: [],
    presetId: undefined,
  });

  const { data: defaultLocations } = useGetDefaultLocationsQuery({
    options: {
      enabled: !useTestData,
    },
  });

  const { data: filtersPresets } = useFetchFiltersPresetsQuery({
    options: {
      initialData: initialFiltersPresets,
      enabled: !useTestData,
    },
  });

  const [selectedFilterPreset, setSelectedFilterPreset] = useState<ConvertFiltersPresetsToInputResult[number] | null>(
    null
  );
  const [editPresetsModalOpen, setEditPresetsModalOpen] = useState(false);

  const triggerChange = (input: Input) => {
    if (input.jobIds.length && (input.levels.length || input.benchmarkLevelIds.length)) {
      onChange(input);
    }
  };

  const mergeInput = (newInput: Partial<Input>) => {
    const updatedInput = {
      ...input,
      ...newInput,
    };

    setInput(updatedInput);
    triggerChange(updatedInput);
  };

  useEffect(() => {
    if (hasInteractedWithLevels || hasInteractedWithLocations || input.presetId || useTestData) {
      return;
    }

    if (!input.jobIds.length) {
      setHasInteractedWithLevels(false);
      setHasInteractedWithLocations(false);
      return;
    }

    setHasInteractedWithLevels(true);
    setHasInteractedWithLocations(true);

    mergeInput({
      ...(!CAN_ACCESS_LEVEL_FRAMEWORKS && {
        levels: uniq(DEFAULT_LEVELS.filter((level) => canAccessLevel(t, user, level))),
      }),
      benchmarkLevelIds: [11, 12, 13], // <-- humm... hard numbered string
      locations: defaultLocations,
    });
  }, [input.jobIds]);

  const submittable = useMemo(() => {
    if (useTestData) {
      return input.jobIds.length && input.levels.length && isDisclaimerReallyAccepted && isDisclaimerAccepted;
    }

    return input.jobIds.length && (input.levels.length || input.benchmarkLevelIds.length) && input.locations.length;
  }, [input, isDisclaimerReallyAccepted, isDisclaimerAccepted]);

  return (
    <HeaderLayout
      title={t("pages.market-data.market-data-form.market-data")}
      breadcrumbs={[
        {
          href: { pathname: "/home" },
          text: t("pages.market-data.market-data-browser.home"),
        },
        {
          text: t("pages.market-data.market-data-browser.market-data"),
        },
      ]}
      extraActions={
        <Stack direction="row" spacing={2}>
          <Button
            component={NextLinkComposed}
            color="gray"
            variant="outlined"
            to={{
              pathname: "/global-salary-converter/[from]/[to]/[amount]",
              query: { from: "berlin", to: "paris", amount: "50000" },
            }}
            newTab
          >
            {t("pages.market-data.market-data-form.global-salary-converter")}
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              onSubmit(input);
            }}
            endIcon={<ArrowForward />}
            disabled={!submittable}
          >
            {useTestData
              ? t("pages.market-data.market-data-form.browse-fake-market-compensation")
              : t("pages.market-data.market-data-form.browse-market-compensation")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={8}>
        {!CAN_ACCESS_LEVEL_FRAMEWORKS && filtersPresets && filtersPresets.length > 0 && (
          <Card>
            <CardHeader title={t("pages.market-data.market-data-form.presets-title")} />

            <MarketDataFormRow title={t("pages.market-data.market-data-form.select-a-preset")}>
              <>
                <Select
                  value={selectedFilterPreset?.id ?? "placeholder"}
                  onChange={(event) => {
                    const preset = filtersPresets.find((preset) => preset.id === event.target.value);

                    if (!preset) return;
                    setSelectedFilterPreset(preset);

                    mergeInput({
                      jobIds: preset.market.jobs.map((job) => job.id),
                      levels: preset.input.levels,
                      locations: preset.market.locations,
                      presetId: preset.id,
                    });
                  }}
                  renderValue={(value) => {
                    if (value === "placeholder") {
                      return (
                        <Typography className="text-slate-500">
                          {t("components.market-data-filters.presets-placeholder")}
                        </Typography>
                      );
                    }

                    return <p>{selectedFilterPreset?.name}</p>;
                  }}
                >
                  {filtersPresets.map((preset) => {
                    const itemValue = preset.id;

                    return (
                      <MenuItem value={itemValue} key={itemValue}>
                        {preset.name}
                      </MenuItem>
                    );
                  })}

                  <MenuItem
                    value="edit-presets"
                    onClick={() => {
                      setEditPresetsModalOpen(true);
                    }}
                    className="justify-center text-sm text-secondary-600"
                  >
                    {t("components.market-data-filters.edit-presets")}
                  </MenuItem>

                  {/* This prevents errors - by having a value for the 'placeholder */}
                  <MenuItem value="placeholder" sx={{ display: "none" }} />
                </Select>

                <DeletePresetModal
                  isOpen={editPresetsModalOpen}
                  onClose={() => {
                    setEditPresetsModalOpen(false);
                  }}
                />
              </>
            </MarketDataFormRow>
          </Card>
        )}
        <Card>
          <CardHeader title={t("pages.market-data.market-data-form.standard-fields")} />

          <MarketDataFormRow title={t("pages.market-data.market-data-form.job-input")}>
            <JobSelector
              enableCustomJobs
              multiple
              className="w-full"
              jobIds={input.jobIds}
              placeholder={t("pages.market-data.market-data-form.job-selector-placeholder", {
                count: input.jobIds.length,
              })}
              onChange={(jobIds) => {
                mergeInput({ jobIds });
              }}
            />
          </MarketDataFormRow>

          <Divider className="relative right-10 w-[110%]" />

          {!CAN_ACCESS_LEVEL_FRAMEWORKS ? (
            <MarketDataFormRow title={t("pages.market-data.market-data-form.level-input")}>
              <LevelSelector
                includeMetaLevels
                multiple
                value={input.levels}
                onChange={(event) => {
                  setHasInteractedWithLevels(true);
                  mergeInput({ levels: event.target.value });
                }}
                useAdvancedLevels={user?.company.useAdvancedLevels ?? true}
              />
            </MarketDataFormRow>
          ) : (
            <MarketDataFormRow title={t("pages.market-data.market-data-form.level-input")}>
              <BenchmarkLevelSelector
                multiple
                value={input.benchmarkLevelIds}
                onChange={(event) => {
                  setHasInteractedWithLevels(true);
                  mergeInput({ benchmarkLevelIds: event.target.value.map((level) => level.id) });
                }}
              />
            </MarketDataFormRow>
          )}

          <Divider className="relative right-10 w-[110%]" />

          <MarketDataFormRow title={t("pages.market-data.market-data-form.location-input")}>
            <LocationSelector
              multiple
              dense={false}
              value={input.locations}
              onChange={(event) => {
                mergeInput({
                  locations: event.target.value,
                });
              }}
            />
          </MarketDataFormRow>

          {useTestData && (
            <Stack spacing={6} className="2xl:max-w-8xl max-w-[100%] flex-1 px-12 2xl:mx-auto 2xl:min-w-[900px]">
              <Stack spacing={12} className="pb-12">
                <Stack spacing={8}>
                  <Stack spacing={2}>
                    <Typography variant="h2">
                      {t("pages.market-data.market-data-form.fake-compensation-information")}
                    </Typography>

                    <Typography>
                      <Trans i18nKey="pages.market-data.market-data-form.data-is-intended-for-testing-purposes">
                        <b />
                      </Trans>
                    </Typography>
                    <Typography>
                      <Trans i18nKey="pages.market-data.market-data-form.salaries-are-not-representative">
                        <b />
                      </Trans>
                    </Typography>

                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="subtitle2">
                        {t("pages.market-data.market-data-form.i-understand")}
                      </Typography>
                      <Switch
                        checked={isDisclaimerAccepted}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setIsDisclaimerAccepted(event.target.checked);
                        }}
                      />
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="subtitle2">
                        {t("pages.market-data.market-data-form.i-read-the-text-above")}
                      </Typography>
                      <Switch
                        checked={isDisclaimerReallyAccepted}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setIsDisclaimerReallyAccepted(event.target.checked);
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          )}
        </Card>
      </Stack>
    </HeaderLayout>
  );
};

type MarketDataFormRowProps = React.PropsWithChildren<{
  title: string;
}>;

const MarketDataFormRow: React.FC<MarketDataFormRowProps> = ({ title, children }) => {
  return (
    <Stack className="p-8" spacing={32} direction="row" alignItems="center">
      <Typography variant="h4" className="w-32 text-neutral-600">
        {title}
      </Typography>

      <Stack className="w-2/3" spacing={4}>
        {children}
      </Stack>
    </Stack>
  );
};
